import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { articles } from '../data/articles';
import { ArrowLeft, Calendar, Linkedin } from 'lucide-react';

const pageContent = {
  en: {
    backToInsights: 'Back to Insights',
    publishedOn: 'Published',
    readTime: 'Read time',
    lastUpdated: 'Last updated',
    scope: 'Scope',
    share: 'Share',
    relatedInsights: 'Related Insights',
    aboutAuthor: 'About the Author',
    authorBio: 'DATEX Engineering team focuses on building production-grade systems for financial infrastructure, AI applications, and enterprise platforms.',
    conversationCta: 'If you\'re designing a production system with similar constraints...',
    startConversation: 'Start a conversation →'
  },
  tr: {
    backToInsights: 'Insights\'a Dön',
    publishedOn: 'Yayınlanma',
    readTime: 'Okuma süresi',
    lastUpdated: 'Son güncelleme',
    scope: 'Kapsam',
    share: 'Paylaş',
    relatedInsights: 'İlgili Yazılar',
    aboutAuthor: 'Yazar Hakkında',
    authorBio: 'DATEX Mühendislik ekibi, finansal altyapı, yapay zeka uygulamaları ve kurumsal platformlar için production seviyesinde sistemler inşa etmeye odaklanır.',
    conversationCta: 'Benzer kısıtlamalarla bir production sistemi tasarlıyorsanız...',
    startConversation: 'Bir konuşma başlatın →'
  },
  de: {
    backToInsights: 'Zurück zu Insights',
    publishedOn: 'Veröffentlicht',
    readTime: 'Lesezeit',
    lastUpdated: 'Zuletzt aktualisiert',
    scope: 'Umfang',
    share: 'Teilen',
    relatedInsights: 'Verwandte Artikel',
    aboutAuthor: 'Über den Autor',
    authorBio: 'Das DATEX Engineering-Team konzentriert sich auf den Aufbau produktionsreifer Systeme für Finanzinfrastruktur, KI-Anwendungen und Unternehmensplattformen.',
    conversationCta: 'Wenn Sie ein Produktionssystem mit ähnlichen Einschränkungen entwerfen...',
    startConversation: 'Gespräch starten →'
  }
};

export function ArticleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = pageContent[currentLanguage] || pageContent.en;
  
  const article = articles[slug];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-28 pb-20 flex items-center justify-center" style={{ background: '#0B0F14' }}>
          <div className="text-center">
            <h1 className="text-2xl font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
              Article not found
            </h1>
            <Link 
              to="/insights" 
              className="text-sm"
              style={{ color: '#1C7C7D' }}
            >
              ← Back to Insights
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const getText = (obj) => obj?.[currentLanguage] || obj?.en || '';
  const content = article.content?.[currentLanguage] || article.content?.en;

  const shareUrl = `https://datexsystems.com/insights/${slug}`;
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  return (
    <>
      <SEO page="articleDetail" articleSlug={slug} />
      <Navbar />
      
      <article className="min-h-screen pt-28 pb-20" style={{ background: '#0B0F14' }}>
        {/* Subtle background */}
        <div 
          className="fixed inset-0 z-0 pointer-events-none opacity-20"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(15, 61, 62, 0.2) 0%, transparent 50%)'
          }}
        />

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8">
          {/* Back Link */}
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-sm mb-10 hover:gap-3 transition-all"
            style={{ color: '#1C7C7D' }}
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backToInsights}
          </Link>

          {/* Article Header */}
          <header className="mb-12">
            {/* Signature Block - Engineering Journal Style */}
            <div 
              className="mb-8 p-5 rounded-lg"
              style={{ 
                background: 'rgba(15, 61, 62, 0.08)',
                border: '1px solid rgba(28, 124, 125, 0.1)'
              }}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-subtle)' }}>
                    {getText(article.category)}
                  </span>
                  <span className="text-xs font-medium" style={{ color: '#1C7C7D' }}>Category</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-subtle)' }}>
                    {article.readTime}
                  </span>
                  <span className="text-xs font-medium" style={{ color: '#1C7C7D' }}>{t.readTime}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-subtle)' }}>
                    {article.lastUpdated || article.publishedAt}
                  </span>
                  <span className="text-xs font-medium" style={{ color: '#1C7C7D' }}>{t.lastUpdated}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-subtle)' }}>
                    {getText(article.scope) || 'Production Systems'}
                  </span>
                  <span className="text-xs font-medium" style={{ color: '#1C7C7D' }}>{t.scope}</span>
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mb-6"
              style={{ color: 'var(--text-primary)', lineHeight: 1.2, letterSpacing: '-0.02em' }}
            >
              {getText(article.title)}
            </h1>

            {/* Excerpt */}
            <p 
              className="text-lg mb-8"
              style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}
            >
              {getText(article.excerpt)}
            </p>

            {/* Meta Bar */}
            <div 
              className="flex flex-wrap items-center gap-6 py-5 border-t border-b"
              style={{ borderColor: 'rgba(28, 124, 125, 0.1)' }}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" style={{ color: 'var(--text-subtle)' }} />
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {article.publishedAt}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {article.author}
                </span>
              </div>
              
              {/* Share */}
              <div className="ml-auto flex items-center gap-3">
                <a
                  href={linkedInShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg transition-colors hover:bg-[rgba(28,124,125,0.1)]"
                  title="Share on LinkedIn"
                >
                  <Linkedin className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                </a>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <div className="prose-custom">
            {/* Introduction */}
            {content?.intro && (
              <p 
                className="text-lg mb-10 leading-relaxed"
                style={{ color: 'var(--text-secondary)' }}
              >
                {content.intro}
              </p>
            )}

            {/* Sections */}
            {content?.sections?.map((section, idx) => (
              <section key={idx} className="mb-12">
                <h2 
                  className="text-xl md:text-2xl font-medium mb-5"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {section.title}
                </h2>
                <div 
                  className="text-base leading-relaxed whitespace-pre-line"
                  style={{ color: 'var(--text-muted)' }}
                  dangerouslySetInnerHTML={{ 
                    __html: section.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: var(--text-primary); font-weight: 500;">$1</strong>')
                      .replace(/\n\n/g, '</p><p class="mb-4">')
                      .replace(/^/, '<p class="mb-4">')
                      .replace(/$/, '</p>')
                  }}
                />
              </section>
            ))}

            {/* Conclusion */}
            {content?.conclusion && (
              <section 
                className="mt-12 pt-8 border-t"
                style={{ borderColor: 'rgba(28, 124, 125, 0.1)' }}
              >
                <p 
                  className="text-lg leading-relaxed"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {content.conclusion}
                </p>
              </section>
            )}
          </div>

          {/* Tags */}
          {article.tags && (
            <div className="mt-12 pt-8 border-t" style={{ borderColor: 'rgba(28, 124, 125, 0.1)' }}>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1.5 rounded text-xs"
                    style={{ 
                      background: 'rgba(28, 124, 125, 0.1)',
                      color: '#1C7C7D'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Author Box */}
          <div 
            className="mt-12 p-6 rounded-xl"
            style={{ 
              background: '#11161C',
              border: '1px solid rgba(28, 124, 125, 0.1)'
            }}
          >
            <h3 
              className="text-xs uppercase tracking-wider mb-3"
              style={{ color: '#1C7C7D', letterSpacing: '0.1em' }}
            >
              {t.aboutAuthor}
            </h3>
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              {article.author}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {t.authorBio}
            </p>
          </div>

          {/* Subtle CTA */}
          <div className="mt-16 pt-8 border-t" style={{ borderColor: 'rgba(28, 124, 125, 0.1)' }}>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {t.conversationCta}{' '}
              <Link
                to="/contact"
                className="font-medium hover:underline transition-colors"
                style={{ color: '#1C7C7D' }}
              >
                {t.startConversation}
              </Link>
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </>
  );
}

export default ArticleDetailPage;
